import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapArray } from "@/lib/api";
import { PageHeader, ViewsPanel } from "@/components/patterns";
import { ScheduleReportsDialog } from "@/components/reports/ScheduleReportsDialog";
import { buildQuery, useDateRange } from "@/components/reports/DateRangeFilter";
import { EMPTY_FILTERS, FILTER_PARAM, ReportFilterBar, type FilterKey, type FilterValues } from "@/components/reports/ReportFilterBar";
import { ExportMenu, type ExportFormat } from "@/components/reports/ExportMenu";
import type { ReportUser } from "@/types/report";
import {
  ActivityTab,
  AgeingTab,
  AssignmentsTab,
  ExecTab,
  ForecastTab,
  HeadTab,
  LostTab,
  PipelineTab,
  ProductsTab,
  QuotesTab,
  SourcesTab,
} from "./ReportTabs";
import { CallsTab } from "./CallsTab";
import { OverviewTab } from "./OverviewTab";

type TabKey =
  | "overview"
  | "execs"
  | "heads"
  | "pipeline"
  | "sources"
  | "lost"
  | "ageing"
  | "activity"
  | "calls"
  | "quotes"
  | "forecast"
  | "products"
  | "assignments";

interface TabDef {
  key: TabKey;
  label: string;
  /** Permission needed to see the tab (matches the API guard) */
  permission?: string;
  /** Tab has its own month / year picker instead of the date range */
  ownPeriod?: boolean;
  /** Tab shows current state, so the period filter does not apply */
  noPeriod?: boolean;
  /** Report-specific filters shown after Team and Salesperson */
  filters?: FilterKey[];
  /** No team / salesperson filter (the report compares teams) */
  noPeople?: boolean;
  /** Report type for the PDF / Excel download */
  download?: string;
}

const TABS: TabDef[] = [
  { key: "overview", label: "Overview", ownPeriod: true, download: "overview" },
  { key: "pipeline", label: "Pipeline", filters: ["pipeline", "lead_source"], download: "pipeline" },
  { key: "forecast", label: "Forecast", noPeriod: true, filters: ["pipeline", "lead_source"], download: "forecast" },
  { key: "lost", label: "Lost analysis", filters: ["pipeline", "lead_source"], download: "lost-analysis" },
  { key: "quotes", label: "Quotes", filters: ["quote_status"], download: "quotes" },
  { key: "products", label: "Products", permission: "report.view_team", filters: ["product", "pipeline", "lead_source"], download: "product-sales" },
  { key: "sources", label: "Lead sources", filters: ["lead_status", "lead_source", "priority"], download: "lead-source" },
  { key: "ageing", label: "Lead ageing", noPeriod: true, filters: ["lead_status", "lead_source", "priority"], download: "lead-ageing" },
  { key: "assignments", label: "Assignments", permission: "lead.assign", filters: ["assign_type"], download: "assignments" },
  { key: "execs", label: "Sales executives", download: "exec-performance" },
  { key: "heads", label: "Sales heads", permission: "report.view_all", noPeople: true, download: "head-performance" },
  { key: "activity", label: "Activity", download: "activity" },
  { key: "calls", label: "Calls", filters: ["outcome"], download: "calls" },
];

/** How the reports are grouped in the panel */
const GROUPS: { heading: string; keys: TabKey[] }[] = [
  { heading: "Summary", keys: ["overview"] },
  { heading: "Revenue", keys: ["pipeline", "forecast", "lost", "quotes", "products"] },
  { heading: "Leads", keys: ["sources", "ageing", "assignments"] },
  { heading: "People", keys: ["execs", "heads", "activity", "calls"] },
];

export function ReportsPage() {
  const { hasPermission, getScope } = useAuth();
  const [params, setParams] = useSearchParams();
  const { preset, range, choosePreset, setRange } = useDateRange("this_month");
  const [people, setPeople] = useState<ReportUser[]>([]);
  /** The Overview's own month / year / day, so its download matches the screen */
  const [overviewParams, setOverviewParams] = useState<Record<string, string | number>>({});

  const scope = getScope("report");
  const visibleTabs = useMemo(() => TABS.filter((t) => !t.permission || hasPermission(t.permission)), [hasPermission]);
  const requested = params.get("tab") as TabKey | null;
  const active: TabDef = visibleTabs.find((t) => t.key === requested) ?? visibleTabs[0] ?? (TABS[0] as TabDef);
  // Every filter lives in the URL: the report and its download both read it from here
  const values: FilterValues = useMemo(() => {
    const v = { ...EMPTY_FILTERS };
    (Object.keys(v) as FilterKey[]).forEach((k) => (v[k] = params.get(k) || ""));
    return v;
  }, [params]);
  const headId = values.head;
  /** Salesperson filter (also the Overview drill-down) */
  const userId = values.user;
  /** Lead ageing: a lead is stuck after this many idle days */
  const days = [3, 7, 14, 30].includes(Number(params.get("days"))) ? Number(params.get("days")) : 7;
  /** Calls: outgoing or incoming; the call-result filter is for outgoing calls only */
  const direction: "out" | "in" = params.get("direction") === "in" ? "in" : "out";
  const ownFilters: FilterKey[] = active.key === "calls" && direction === "in" ? [] : active.filters || [];
  const shown: FilterKey[] = [...(active.noPeople ? [] : (["head", "user"] as FilterKey[])), ...ownFilters];
  const heads = useMemo(() => (scope === "all" ? people.filter((u) => u.role_code === "SALES_HEAD") : []), [people, scope]);
  const canExport = hasPermission("report.export");
  const [scheduleOpen, setScheduleOpen] = useState(false);

  // Salespeople the caller can report on; only Super Admin compares several Sales Heads, so only they get the team filter
  useEffect(() => {
    if (scope !== "all" && scope !== "team") return;
    api
      .get("/api/Report/Users")
      .then((res) => setPeople(unwrapArray<ReportUser>(res).filter((u) => u.role_code === "SALES_HEAD" || u.role_code === "SALES_EXEC")))
      .catch(() => setPeople([]));
  }, [scope]);

  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    // Team and salesperson carry over; a report's own filters don't
    for (const k of ["lead_status", "lead_source", "priority", "outcome", "pipeline", "product", "quote_status", "assign_type", "days", "direction"]) next.delete(k);
    setParams(next, { replace: true });
  };

  const setFilter = (key: FilterKey, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // A salesperson outside the newly chosen team no longer applies
    if (key === "head" && value && values.user) {
      const u = people.find((p) => p.id === values.user);
      if (u && u.id !== value && u.manager_id !== value) next.delete("user");
    }
    setParams(next, { replace: true });
  };

  const clearFilters = () => {
    const next = new URLSearchParams(params);
    (Object.keys(EMPTY_FILTERS) as FilterKey[]).forEach((k) => next.delete(k));
    setParams(next, { replace: true });
    choosePreset("this_month");
  };

  /** The filters this report uses, as API parameters */
  const filterQuery = () => Object.fromEntries(shown.filter((k) => values[k]).map((k) => [FILTER_PARAM[k], values[k]]));
  const activeFilters = shown.filter((k) => values[k]).length;

  /** Set (or clear, with "") one URL parameter, plus any others given */
  const setParam = (key: string, value: string, more: Record<string, string> = {}) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries({ ...more, [key]: value })) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next);
  };

  /** Settings a report has besides its filters, which its files must also follow */
  const settings = (): Record<string, string | number> =>
    active.key === "overview" ? overviewParams : active.key === "ageing" ? { days } : active.key === "calls" ? { direction: direction.toUpperCase() } : {};
  const fileUrl = (format: ExportFormat) => `/api/Report/Download/${active.download}${buildQuery({ format, from: range.from, to: range.to, ...filterQuery(), ...settings() })}`;

  // The report's own filters (beyond team and salesperson) as API parameters
  const extra = Object.fromEntries(ownFilters.filter((k) => values[k]).map((k) => [FILTER_PARAM[k], values[k]]));
  const tabProps = { range, headId, userId, extra };
  const description =
    scope === "all"
      ? "Company-wide numbers for every Sales Head and Sales Executive."
      : scope === "team"
        ? "Numbers for you and the Sales Executives in your team."
        : "Your own numbers.";

  return (
    <div className="space-y-4">
      <ViewsPanel
        title="Reports"
        subtitle={scope === "all" ? "The whole company" : scope === "team" ? "You and your team" : "Your own numbers"}
        active={active.key}
        onSelect={(k) => setTab(k as TabKey)}
        groups={GROUPS.map((g) => ({
          heading: g.heading,
          items: g.keys.map((k) => ({ key: k, label: TABS.find((t) => t.key === k)!.label, hidden: !visibleTabs.some((t) => t.key === k) })),
        }))}
        footer={
          hasPermission("target.read") ? (
            <div className="mt-3 px-2.5">
              <Link to="/targets" className="flex h-8 items-center rounded-md px-2.5 text-[13px] font-medium text-primary hover:bg-muted">
                Targets →
              </Link>
            </div>
          ) : undefined
        }
      />

      <PageHeader
        title={active.label}
        actions={
          <>
            <Button variant="outline" size="sm" className="hidden md:inline-flex" onClick={() => setScheduleOpen(true)}>
              Email me reports
            </Button>
            {canExport && <ExportMenu fileUrl={active.download ? fileUrl : undefined} activeFilters={activeFilters} />}
          </>
        }
      />
      <ScheduleReportsDialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} />

      <p className="text-[13px] text-muted-foreground">{description}</p>

      <ReportFilterBar
        date={active.ownPeriod || active.noPeriod ? null : { preset, range, onPreset: choosePreset, onRange: setRange, isDefault: preset === "this_month" }}
        show={shown}
        values={values}
        onChange={setFilter}
        onClear={clearFilters}
        heads={heads}
        people={people}
        openLeadsOnly={active.key === "ageing"}
      />
      {active.noPeriod && <p className="text-xs text-muted-foreground">Shows open deals and leads as of now.</p>}

      {active.key === "overview" && (
        <OverviewTab
          headId={headId}
          userId={userId}
          onParams={setOverviewParams}
          onPickUser={(id) => setParam("user", id)}
          onPickHead={scope === "all" ? (id) => setParam("user", "", { head: id }) : undefined}
          onBack={() => setParam("user", "")}
        />
      )}
      {active.key === "execs" && <ExecTab {...tabProps} />}
      {active.key === "heads" && (
        <HeadTab
          {...tabProps}
          onPickHead={(id) => {
            const next = new URLSearchParams(params);
            next.set("tab", "execs");
            next.set("head", id);
            setParams(next, { replace: true });
          }}
        />
      )}
      {active.key === "pipeline" && <PipelineTab {...tabProps} />}
      {active.key === "forecast" && <ForecastTab {...tabProps} />}
      {active.key === "sources" && <SourcesTab {...tabProps} leadStatus={values.lead_status} leadSource={values.lead_source} priority={values.priority} />}
      {active.key === "lost" && <LostTab {...tabProps} />}
      {active.key === "ageing" && <AgeingTab {...tabProps} days={days} onDays={(d) => setParam("days", d === 7 ? "" : String(d))} />}
      {active.key === "activity" && <ActivityTab {...tabProps} />}
      {active.key === "calls" && <CallsTab {...tabProps} outcome={values.outcome} direction={direction} onDirection={(d) => setParam("direction", d === "in" ? "in" : "", { outcome: "" })} />}
      {active.key === "quotes" && <QuotesTab {...tabProps} />}
      {active.key === "products" && <ProductsTab {...tabProps} />}
      {active.key === "assignments" && <AssignmentsTab {...tabProps} />}
    </div>
  );
}
